"use client";

import * as React from "react";
import { cn } from "../../lib/utils";

// Hook to check archival mode (lazy import to avoid circular deps)
let useArchivalModeHook: (() => { isArchivalMode: boolean }) | null = null;
try {
  const archivalContext = require("@/contexts/ArchivalModeContext");
  useArchivalModeHook = archivalContext.useArchivalMode;
} catch {
  // Context not available
}

export interface SwitchProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, checked = false, onCheckedChange, disabled, ...props }, ref) => {
    // Check archival mode
    let isArchivalMode = false;
    try {
      if (useArchivalModeHook) {
        const archival = useArchivalModeHook();
        isArchivalMode = archival.isArchivalMode;
      }
    } catch {
      // Context not available
    }
    
    // Disable switch in archival mode - NO EXCEPTIONS
    const effectiveDisabled = disabled || isArchivalMode;
    
    return (
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={effectiveDisabled}
        ref={ref}
        className={cn(
          "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-50",
          checked ? "bg-[#4da3ff]" : "bg-white/20",
          className
        )}
        onClick={effectiveDisabled ? undefined : () => onCheckedChange?.(!checked)}
        {...props}
      >
        <span
          className={cn(
            "pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform",
            checked ? "translate-x-5" : "translate-x-0"
          )}
        />
      </button>
    );
  }
);
Switch.displayName = "Switch";

export { Switch };

